import React, { useContext, useState } from 'react';

import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Betterflies4 } from '../../../../../assets/icons/v3/betterflies-4.svg';
import { ReactComponent as ConcernBuddy } from '../../../../../assets/icons/v3/buddy-concern.svg';
import BdsTextButton from '../../../../../shared/components/v3/Buttons/BdsTextButton/BdsTextButton';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { LOGIN_ROUTE } from '../../../../../shared/constants/auth';
import { RECAPTCHA_SITE_KEY } from '../../../../../shared/constants/constants';
import { ARROW_LEFT_ICON } from '../../../../../shared/constants/images';
import { CLICK_ACTION, RESTORE_PASSWORD } from '../../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../../shared/constants/toast';
import { SessionContext } from '../../../../../shared/context/auth/authContext';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../../shared/translations/auth';
import AuthError from '../../../sections/AuthError/AuthError';
import CreatePassword from '../../../sections/CreatePassword/CreatePassword';
import VerificationCode from '../../../sections/VerificationCode/VerificationCode';
import './ForgotPasswordCard.scss';

interface ForgotPasswordCardProps {
  email: string;
}

const ForgotPasswordCard = ({ email }: ForgotPasswordCardProps) => {
  const [step, setStep] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState(false);

  const { sessionLogin } = useContext(SessionContext);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChangeStep = (newStep: number) => {
    setStep(newStep);
  };

  const handleGoBack = () => {
    if (step === 0) {
      navigate(LOGIN_ROUTE);
      return;
    }
    setStep(step - 1);
  };

  const backToLogin = () => {
    navigate(LOGIN_ROUTE);
  };

  const handleSubmitVerificationCode = (code: string) => {
    setVerificationCode(code);
    setStep(1);
  };

  const handleBackFromErrorCard = () => {
    setStep(0);
    setShowErrorCard(false);
  };

  const resetCustomError = () => {
    setInputError(false);
  };

  const resendEmail = async () => {
    await resetPassword({ username: email });
    setStep(0);
    setShowErrorCard(false);
  };

  const handleLogin = async (userPassword: string) => {
    window.grecaptcha.ready(async () => {
      setLoading(true);
      const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
      await sessionLogin(email, userPassword, recaptchaToken);
      setLoading(false);
    });
  };

  const handleResetPassword = async (userPassword: string) => {
    try {
      setLoading(true);
      TagManagerService.pushEvent(CLICK_ACTION, RESTORE_PASSWORD);
      await confirmResetPassword({ username: email, confirmationCode: verificationCode, newPassword: userPassword });
      await handleLogin(userPassword);
    } catch (error: any) {
      if (error.toString().includes('CodeMismatchException')) {
        setShowErrorCard(true);
      } else if (error.toString().includes('PasswordHistoryPolicyViolationException')) {
        setInputError(true);
      } else {
        setErrorToast(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (showErrorCard) {
    return (
      <AuthError
        title={TRANSLATIONS_AUTH.passwordRecoveryError.title}
        description={TRANSLATIONS_AUTH.passwordRecoveryError.description}
        Icon={ConcernBuddy}
        primaryButton={{
          text: TRANSLATIONS_AUTH.passwordRecoveryError.primaryButton,
          action: handleBackFromErrorCard
        }}
        secondaryButton={{
          text: TRANSLATIONS_AUTH.passwordRecoveryError.secondaryButton,
          action: resendEmail
        }}
        handleGoBack={handleBackFromErrorCard}
      />
    );
  }

  return (
    <div className="forgot-password-card" data-experience="new">
      <div className="auth-card-go-back">
        <BdsTextButton action={handleGoBack} iconLeft={ARROW_LEFT_ICON}>
          {t(TRANSLATIONS_AUTH.goBack)}
        </BdsTextButton>
      </div>
      <div className="vc-betterfly-image">
        <Betterflies4 className="image" />
      </div>
      <div className={`collapsable-section ${step === 0 ? 'show' : 'hide'}`}>
        <VerificationCode
          email={email}
          codeType="password-reset"
          handleGoBack={backToLogin}
          handleNext={handleSubmitVerificationCode}
        />
      </div>
      <div className={`collapsable-section ${step === 1 ? 'show' : 'hide'}`}>
        <CreatePassword
          verificationCode={verificationCode}
          handleGoBack={handleChangeStep}
          onSubmit={handleResetPassword}
          loading={loading}
          customError={inputError ? t(TRANSLATIONS_AUTH.forgotPassword.samePass) : undefined}
          resetCustomError={resetCustomError}
        />
      </div>
      <Toast
        title={t(GENERIC_ERROR_TOAST.title)}
        description={t(GENERIC_ERROR_TOAST.description)}
        icon={GENERIC_ERROR_TOAST.icon}
        open={errorToast}
        button={true}
        handleClose={() => setErrorToast(false)}
      />
    </div>
  );
};

export default ForgotPasswordCard;
